import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  StorefrontCurrencyClient,
  StorefrontCurrencyDto,
} from "@lib/ShiOneClient";
import { useApi } from "@utils/useApi";
import { createCurrencyFormatter } from "shared-ui/src/functions/currency";
import { useOrgUnitIdCurrent } from "../../../../../auth/useOrgUnitIdCurrent";

/** Query key for the current storefront's currency. */
export const STOREFRONT_CURRENCY_QUERY_KEY = ["storefront-currency"];

/**
 * A storefront currency with every field guaranteed present. The generated DTO types all fields as
 * optional, so this hook normalizes them once rather than making each price surface guard.
 */
export interface StorefrontCurrency {
  id: number;
  code: string;
  symbol: string;
  locale: string;
}

/** Used until the org unit's currency loads, and for any field the API omits. */
const USD_FALLBACK: StorefrontCurrency = {
  id: 1,
  code: "USD",
  symbol: "$",
  locale: "en-US",
};

/**
 * Resolves the currency the current org unit transacts in, with a ready-to-use formatter.
 *
 * Prices returned by the BFF are already expressed in this currency (Commerce converts supplier
 * pricing into the org unit's currency), so this only supplies the label -- never a conversion.
 *
 * The org unit's currency changes only when the user switches storefront, so the query is cached
 * for the session and shared by every price-rendering surface.
 *
 * Pass `orgUnitId` to label a managed storefront other than the current one. The current org unit
 * always uses the open endpoint: the per-org-unit endpoint requires storefront-admin access, which
 * not every price surface's user has.
 */
export default function useStorefrontCurrency(orgUnitId?: number | null) {
  const api = useApi(StorefrontCurrencyClient);
  const currentOrgUnitId = useOrgUnitIdCurrent();
  const validOrgUnitId =
    typeof orgUnitId === "number" &&
    Number.isInteger(orgUnitId) &&
    orgUnitId > 0 &&
    orgUnitId !== currentOrgUnitId
      ? orgUnitId
      : undefined;
  const queryKey =
    validOrgUnitId !== undefined
      ? [...STOREFRONT_CURRENCY_QUERY_KEY, validOrgUnitId]
      : STOREFRONT_CURRENCY_QUERY_KEY;

  const { data, isLoading } = useQuery<StorefrontCurrencyDto>({
    queryKey,
    queryFn: () =>
      validOrgUnitId !== undefined
        ? api.getStorefrontCurrencyForOrgUnit(validOrgUnitId)
        : api.getStorefrontCurrency(),
    staleTime: Infinity,
  });

  const currency = useMemo<StorefrontCurrency>(
    () => ({
      id: data?.id ?? USD_FALLBACK.id,
      code: data?.code || USD_FALLBACK.code,
      symbol: data?.symbol || USD_FALLBACK.symbol,
      locale: data?.locale || USD_FALLBACK.locale,
    }),
    [data?.id, data?.code, data?.symbol, data?.locale],
  );

  const formatter = useMemo(
    () => createCurrencyFormatter(currency.locale, currency.code, currency.symbol),
    [currency.locale, currency.code, currency.symbol],
  );

  // Named formatAmount rather than formatCurrency to avoid colliding with the existing USD-only
  // utils/formatters/formatCurrency, which has the opposite currency semantics.
  // Stable identity so consumers that list it in a dependency array (useSubmitLeasingInquiry) are
  // not invalidated on every render.
  const formatAmount = useCallback(
    (value?: number | null) => formatter.format(value ?? 0),
    [formatter],
  );

  return { currency, isLoading, formatAmount };
}
